import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ACTIONS } from "../../common/Constants";
import Table, { CellTmpl, DEFAULT_PAGE_SIZE, TitleTmpl, reactFormatter } from "../../common/Table";
import { useDeleteComplianceSchedule, useGetAllComplianceActivities } from "../../../backend/compliance";
import TableActions, { ActionButton } from "../../common/TableActions";
import { USER_PRIVILEGES } from "../UserManagement/Roles/RoleConfiguration";
import { hasUserAccess, isAdmin, isComplianceUser } from "../../../backend/auth";
import Icon from "../../common/Icon";
import { COMPLIANCE_ACTIVITY_INDICATION, ComplianceActivityStatus, ComplianceStatusMapping } from "../../../constants/Compliance.constants";
import MastersLayout from "../Masters/MastersLayout";
import ConfirmModal from "../../common/ConfirmModal";
import AddEditNotice from "./AddEditNotice";
import styles from "./Notices.module.css";
import NoticeFilters from "./NoticeFilters";
import ComplianceAssignUser from "../ComplianceMasters/ComplianceAssignUser";
import OptionalLocations from "../../common/OptionalLocations";
import ComplianceScheduleAdvanceFilter from "../ComplianceMasters/ComplianceScheduleAdvanceFilter";
import { API_RESULT, ERROR_MESSAGES } from "../../../utils/constants";
import { toast } from "react-toastify";
import PageLoader from "../../shared/PageLoader";
import Location from "../../common/Location";
import ComplianceOwnerFilters from "../ComplianceOwner/ComplianceOwnerFilters";

const INCLUDE_ASSOCIATIONS = [
  'company', 'associate_company','location', 'act', 'rule', 'activity', 
  'department', 'compliance_owner', 'compliance_manager', 'vertical'
]

const INCLUDE_ASSOCIATION_COLUMNS = {
  company: {
    only: ['id','name','code']
  },
  associate_company: {
    only: ['id','name','code','establishment_type']
  },
  location: {
    only: ['id','name','code']
  },
  act: {
    only: ['id','name', 'description']
  },
  rule: {
    only: ['id','name', 'rule_no', 'section_no', 'description']
  },
  activity: {
    only: ['id','name','activity_type', 'periodicity']
  },
  department: {
    only: ['id','name']
  },
  compliance_owner: {
    only: ['id','name']
  },
  compliance_manager: {
    only: ['id','name']
  },
  vertical: {
    only: ['id','name']
  },
  act_state_mapping: {
    include: ['rule_compliance_detail']
  }
}

const DEFAULT_PAYLOAD = {
    per: DEFAULT_PAGE_SIZE,
    page: 1,
    filters: {},
    search: '',
    includes: INCLUDE_ASSOCIATIONS,
    include: INCLUDE_ASSOCIATION_COLUMNS
}

const SortFields: any = {
    'act.name': 'act_name',
    'rule.name': 'rule_name',
    'activity.name': 'activity_name',
    'activity.type': 'activity_activity_type',
    'associate_company.name': 'associate_company_name',
    'compliance_manager.name': 'compliance_manager_name',
    'compliance_owner.name': 'compliance_owner_name',
    'vertical.name': 'vertical_name',
    'department.name': 'department_name',
    'location.name': 'location_name'
};

function ManageNotices() {
    const [breadcrumb] = useState([
        { id: 'home', label: 'Home', path: '/' },
        { id: 'notices', label: 'Notices', path: '/notices' }
    ]);
    const [action, setAction] = useState(ACTIONS.NONE);
    const [notice, setNotice] = useState<any>(null);
    const [data, setData] = useState<any>();
    const [params, setParams] = useState<any>();
    const [filters, setFilters] = useState<any>();
    const filterRef: any = useRef();
    filterRef.current = filters;
    const [locationFilters, setLocationFilter] = useState<any>();
    const lfRef: any = useRef();
    lfRef.current = locationFilters;
    const [advaceSearchFilters, setAdvanceSearchFilters] = useState<any>();
    const afRef: any = useRef();
    afRef.current = advaceSearchFilters;
    const [payload, setPayload] = useState<any>(null);
    const payloadRef: any = useRef();
    payloadRef.current = payload;
    const { activities, total, isFetching, refetch } = useGetAllComplianceActivities(payload, hasFilters(null));
    const { deleteComplianceSchedule, deleting } = useDeleteComplianceSchedule(({ key, value }: any) => {
        if (key === API_RESULT.SUCCESS) {
            toast.success('Notice deleted successfully.');
            handleCancel(true);
        } else {
            toast.error(value || ERROR_MESSAGES.DEFAULT)
        }
    });

    const buttons: ActionButton[] = [{
        label: 'Add Notice',
        name: 'addNew',
        privilege: USER_PRIVILEGES.ADD_NOTICE,
        icon: 'plus',
        action: () => setAction(ACTIONS.ADD)
    }];

    function hasFilters(ref: any) {
        const _filters = (ref ? ref.current : { ...(payloadRef.current || {}) }.filters) || {};
        const hasNoticefilter = _filters['is_notice_eq'] || false;
        if (isAdmin()) {
            return hasNoticefilter;
        } else if (isComplianceUser()) {
            const hasCompanyFilter = _filters['company_id_eq'] || false;
            return hasNoticefilter && hasCompanyFilter;
        } else {
            const hasLocationFilter = _filters['location_id_eq'];
            return hasNoticefilter && hasLocationFilter;
        }
    }

    function ActionColumnElements({ cell }: any) {
        const row = cell.getData();
        return (
            <div className="d-flex flex-row align-items-center position-relative h-100 gap-3">
                <Icon type="button" name={'eye'} text={'View'} data={row} action={() => {
                    setNotice(row);
                    setAction(ACTIONS.VIEW)
                }} />
                {/* <Icon name="download" text={'Download Form'} data={row} action={downloadForm} /> */}
                {
                    hasUserAccess(USER_PRIVILEGES.ASSIGN_NOTICE) &&
                    <Icon type="button" name={'people'} text={'Assign'} data={row} action={() => {
                        setNotice(row);
                        setAction(ACTIONS.ASSIGN_SINGLE)
                    }} disabled={![ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT].includes(row.status)} />
                }
                {
                    hasUserAccess(USER_PRIVILEGES.EDIT_NOTICE) &&
                    <Icon type="button" name={'pencil'} text={'Edit'} data={row} action={() => {
                        setNotice(row);
                        setAction(ACTIONS.EDIT)
                    }} disabled={![ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT].includes(row.status)} />
                }
                {
                    // hasUserAccess(USER_PRIVILEGES.DELETE_NOTICE) &&
                    // <Icon type="button" name={'trash'} text={'Delete'} data={row} action={() => {
                    //     setNotice(row);
                    //     setAction(ACTIONS.DELETE)
                    // }} />
                }

            </div>
        )
    }

    function FormStatusTmpl({ cell }: any) {
        const status = cell.getValue();
        return (
            <div className="d-flex align-items-center position-relative">
                <span className="ellipse" style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }}>{ComplianceStatusMapping[status] || status}</span>
            </div>
        )
    }

    function FormIndicationTmpl({ cell }: any) {
        const status = cell.getValue();
        return (
            <div className="d-flex align-items-center">
                <Icon name="circle" style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }} text={ComplianceStatusMapping[status]} />
            </div>
        )
    }

    function MonthTmpl({ cell }: any) {
        const row = cell.getData();
        return (
            <div className="ellipse two-lines">{row.month} ({row.year})</div>
        )
    }

    function LocationTmpl({ cell }: any) {
        const { company, associate_company, location } = cell.getData();
        return (
            <>{company.code}-{associate_company.code}-{location.code}</>
        )
    }

    function DueDateTmpl({ cell }: any) {
        const value = cell.getValue();
        return (
            <span className="text-warn" >{dayjs(value).format('DD-MM-YYYY')}</span>
        )
    }

    const columns = [

        {
            title: "", width: 30, field: "status",
            headerSort: false,
            formatter: reactFormatter(<FormIndicationTmpl />)
        },
        {
            title: "Month (Year)", field: "month", width: 120,
            formatter: reactFormatter(<MonthTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Owner", field: "compliance_owner.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Manager", field: "compliance_manager.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Vertical", field: "vertical.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Department", field: "department.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Location", field: "location", headerSort: false,
            formatter: reactFormatter(<LocationTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1, minWidth: 140
        },
        {
            title: "Act", field: "act.name", widthGrow: 2, minWidth: 140, maxWidth: 300,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Rule", field: "rule.name", widthGrow: 2, minWidth: 140, maxWidth: 300,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Activity", field: "activity.name", minWidth: 140, maxWidth: 300,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 2
        },
        {
            title: "Activity Type", field: "activity.activity_type",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            minWidth: 140, maxWidth: 200
        },
        {
            title: "Due Date", field: "due_date", width: 120,
            formatter: reactFormatter(<DueDateTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Evidence Status", field: "status", width: 160,
            formatter: reactFormatter(<FormStatusTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "", width: 160,
            headerSort: false,
            formatter: reactFormatter(<ActionColumnElements />)
        },
    ]

    const [tableConfig] = useState({
        paginationMode: 'remote',
        ajaxRequestFunc,
        columns,
        rowHeight: 'auto',
        minHeight: '100%',
        selectable: false,
        paginate: true,
        bufferSpacing: 20,
        initialSort: [{ column: 'startDate', dir: 'desc' }]
    });

    function formatApiResponse(list: any[], totalRecords: number) {
        const { per, page } = params || {};
        const tdata = {
          data: list,
          total: totalRecords,
          last_page: Math.ceil(totalRecords / (per || 1)) || 1,
          page: page || 1
        };
        setData(tdata);
        return tdata;
    }

    function ajaxRequestFunc(url: any, config: any, pagination_params: any) {
        const { field, dir } = (pagination_params.sort || [])[0] || {};
        const sort_column = SortFields[field] || field || 'start_date';
        const sort_order  = dir || 'desc';
        const _params = {
          per: pagination_params.size,
          page: pagination_params.page,
          sorts: [`${sort_column} ${sort_order}`],
          includes: INCLUDE_ASSOCIATIONS,
          include: INCLUDE_ASSOCIATION_COLUMNS
        };
        setParams(_params);
        const _payload = { ...DEFAULT_PAYLOAD, ...payloadRef.current };
        const _filters = _payload.filters || {};
        setPayload({
            ...DEFAULT_PAYLOAD,
            ..._params,
            filters: addDefaultFilters(_filters)
        });
        return Promise.resolve(formatApiResponse(activities, total));
    }

    function handlePageNav(_pagination: any) {
        const _params = Object.assign( params, _pagination );
        setParams({ ..._params });
        setPayload({ ...payload, ..._params, filters: addDefaultFilters(payload.filters) })
    }

    function addDefaultFilters(_filters: any) {
        _filters = _filters || {};
        const isNotice = _filters['is_notice_eq'];
        if (!isNotice) {
            _filters['is_notice_eq'] = true;
        }
        return _filters;
    }

    function submitCallback() {
        setAction(ACTIONS.NONE);
        setNotice(null);
        refetch();
    }

    function onLocationChange(event: any) {
        const keys = Object.keys(event);
        const _filters:any = {};
        keys.forEach((key) => {
          if(!!event[key]) {
            _filters[key] = event[key]
          }
        })
        setLocationFilter(_filters);
    }

    function onLocationChangeForAuditUser({company, associate_company, location} : any) {
        const _filters: any = {};
        if (company) {
            _filters['company_id_eq'] = company;
        }
        if (associate_company) {
            _filters['associate_company_id_eq'] = associate_company;
        }
        if (location) {
            _filters['location_id_eq'] = location;
        }
        setLocationFilter(_filters);
    }

    function onLocationChangeForComplianceUser(event: any) {
        setLocationFilter(event);
    }

    function search(event: any) {
        const _filters: any = {};
        const keys = Object.keys(event || {});
        if (keys.length > 0) {
          keys.forEach(key => {
            _filters[key] = event[key];
          })
        }
        setAdvanceSearchFilters(_filters);
    }

    function performDelete() {
        deleteComplianceSchedule([notice.id]);
    }

    function handleExport() {
        setNotice({ ...payload, pagination: null });
    }

    function handleCancel(reload = false) {
        setAction(ACTIONS.NONE);
        setNotice(undefined);
        if (reload) {
            refetch();
        }
    }

    useEffect(() => {
        if (locationFilters) {
            setData({ ...data, page: 1 });
            setParams({ ...params, page: 1 });
            setPayload({
                ...DEFAULT_PAYLOAD,
                sorts: ['month desc'],
                ...params,
                filters: addDefaultFilters({
                    ...locationFilters,
                    ...(afRef.current || {})
                }),
                page: 1
            });
        }
    }, [locationFilters]);

    useEffect(() => {
        if (advaceSearchFilters) {
            setData({ ...data, page: 1 });
            setParams({ ...params, page: 1 });
            setPayload({
                ...DEFAULT_PAYLOAD,
                sorts: ['month desc'],
                ...params,
                filters: addDefaultFilters({
                    ...(lfRef.current || {}),
                    ...advaceSearchFilters
                }),
                page: 1
            });
        }
    }, [advaceSearchFilters]);

    useEffect(() => {
        if (!isFetching && payload) {
            setTimeout(() => {
                setData(formatApiResponse(activities, total));
            });
        }
    }, [isFetching]);

    return (
        <>
            <MastersLayout title="Notices" breadcrumbs={breadcrumb}>
                <div className="d-flex flex-column mx-0">
                    <div className="card shadow d-flex flex-row justify-content-center m-3 p-3">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-end">
                                {
                                    isAdmin() ?
                                        <OptionalLocations onChange={onLocationChange} /> :
                                        <>
                                            {
                                                isComplianceUser() ?
                                                    <ComplianceOwnerFilters onFilterChange={onLocationChangeForComplianceUser} forNotices={true} /> :
                                                    <Location onChange={onLocationChangeForAuditUser} />
                                            }
                                        </>
                                }
                                <div>
                                    <ComplianceScheduleAdvanceFilter onChange={search} filterForNotice={true} />
                                </div>
                                <TableActions buttons={buttons} />
                            </div>
                        </div>
                    </div>
                    <div className={styles.tableWrapper}>
                        <div className={styles.flexibleContainer}>
                            <Table data={data} options={tableConfig} isLoading={isFetching} onPageNav={handlePageNav} />
                        </div>
                    </div>
                </div>
            </MastersLayout>
            {
                [ACTIONS.ADD, ACTIONS.EDIT, ACTIONS.VIEW].includes(action) &&
                <AddEditNotice action={action} data={action !== ACTIONS.ADD ? notice : null}
                    onClose={() => setAction(ACTIONS.NONE)} onSubmit={submitCallback} />
            }
            {
                action === ACTIONS.ASSIGN_SINGLE && Boolean(notice) &&
                <ComplianceAssignUser action={action} activities={[notice]} onCancel={handleCancel} />
            }
            {
                action === ACTIONS.DELETE &&
                <ConfirmModal title={'Delete Notice'} onSubmit={performDelete} onClose={() => setAction(ACTIONS.NONE)}>
                    <div className="text-center mb-4">Are you sure you want to delete the notice ?</div>
                </ConfirmModal>
            }
            {deleting && <PageLoader />}
        </>
    )
}

export default ManageNotices;
